import React, { useState, useEffect, createRef } from 'react';
import { useResource, useFetcher, useInvalidator } from 'rest-hooks';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { Grid, FlexRow, FlexBaseCol, H2, H3, Subtext, Form, CommentForm } from '@contently/toolkit';
import AnnotationResource from '../../resources/AnnotationResource';
import ContentResource from '../../resources/ContentResource';
import CommentResource from '../../resources/CommentResource';
import useInvalidateCacheOnUnmount from '../../hooks/useInvalidateCacheOnUnmount';
import useOutsideMultipleElClick from '../../hooks/useOutsideMultipleElClick';
import { getAnnotationParams } from './utils';
import filterReducer from '../../utils/filterReducer';
import CommentWrapper from './CommentWrapper';
import DropdownWrapper from './DropdownWrapper';
import { updateAnnotationListCacheBust } from '../../store/textEditorReducer';
import useStoryContext from '../../hooks/useStoryContext';

const StyledDiv = styled.div`
  border: 1px solid #d4d4d4;
  margin-bottom: 10px;
`;

export default function Comments() {
  const { mentionableUsersBasicInfo, id } = useStoryContext();
  const storyId = parseInt(id, 10);
  const comments = useResource(CommentResource.listShape(), CommentResource.forStory(storyId));
  const storyContents = useResource(ContentResource.listShape(), {
    contentable_id: storyId
  });
  const annotationParams = getAnnotationParams(storyContents.map(sC => sC.id));
  const annotations = useResource(AnnotationResource.listShape(), annotationParams);
  useInvalidateCacheOnUnmount(CommentResource.listShape(), CommentResource.forStory(storyId));
  useInvalidateCacheOnUnmount(AnnotationResource.listShape(), annotationParams);
  const createComment = useFetcher(CommentResource.createShape());
  const [formVisible, setFormVisible] = useState(false);
  const [activeComment, setActiveComment] = useState(null);
  const [filterState, setFilterState] = useState('Open comments');
  const { annotationListCacheBust } = useSelector(state => state.textEditor);
  const invalidateAnnotationList = useInvalidator(AnnotationResource.listShape());
  const dispatch = useDispatch();
  const commentArr = [
    ...comments,
    ...annotations.map(a => ({ ...a.comment, uuid: a.markId, highlightedText: a.highlightedText }))
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const commentsRef = commentArr.map(() => createRef());

  useOutsideMultipleElClick(commentsRef, commentId => setActiveComment(commentId));

  useEffect(() => {
    if (commentArr.length === commentsRef.length) return;
    if (commentArr.length > commentsRef.length) commentsRef.push(createRef());
    else commentsRef.pop();
  }, [commentArr, commentsRef]);

  useEffect(() => {
    if (annotationListCacheBust) {
      invalidateAnnotationList(annotationParams);
      dispatch(updateAnnotationListCacheBust(false));
    }
  }, [annotationListCacheBust, annotationParams, dispatch, invalidateAnnotationList]);

  const toggleVisible = () => {
    setFormVisible(prev => !prev);
  };

  const handleSave = e => {
    createComment({}, { ...e, commentable_type: 'Story', commentable_id: storyId }, [
      [
        CommentResource.listShape(),
        CommentResource.forStory(storyId),
        (newCommentId, commentIds) => {
          return [...(commentIds || []), newCommentId];
        }
      ]
    ]);

    toggleVisible();
  };

  const handleCancel = e => {
    if (e) e.preventDefault();
    setActiveComment(null);
  };

  const renderComments = () => {
    const filteredArr = filterReducer(filterState, commentArr);

    if (filteredArr.length === 0) {
      return <H3>There are no comments matching your filters.</H3>;
    }
    return filteredArr.map((comment, index) => {
      return (
        <StyledDiv
          id={comment.id}
          key={comment.id}
          style={{ width: '100%' }}
          ref={commentsRef[index]}
        >
          <CommentWrapper
            handleCancel={handleCancel}
            active={comment.id === activeComment}
            key={comment.id}
            comment={comment}
            storyId={storyId}
            users={mentionableUsersBasicInfo}
          />
        </StyledDiv>
      );
    });
  };

  return (
    <Grid>
      <FlexRow margin="small">
        <FlexBaseCol>
          <H2>Comments</H2>
        </FlexBaseCol>
        <FlexBaseCol textAlign="right">
          {!formVisible && (
            <Form.Button size="small" onClick={toggleVisible} variant="secondary">
              create comment
            </Form.Button>
          )}
        </FlexBaseCol>
        <Subtext>
          To comment on a specific part of the story, highlight that section first. You can mention
          other people by typing @ and their name.
        </Subtext>
      </FlexRow>

      {formVisible && (
        <FlexRow>
          <FlexBaseCol>
            <FlexRow>
              <CommentForm
                width="100%"
                body=""
                onCancel={toggleVisible}
                onSubmit={e => handleSave(e)}
                users={mentionableUsersBasicInfo}
              />
            </FlexRow>
          </FlexBaseCol>
        </FlexRow>
      )}
      <FlexRow>
        <DropdownWrapper filterState={filterState} setFilterState={setFilterState} />
      </FlexRow>
      <FlexRow>{renderComments()}</FlexRow>
    </Grid>
  );
}



// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/Comments.jsx